import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight, User } from "lucide-react";
import { blogPosts, getPostBySlug } from "@/data/blogPosts";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";

const formatDate = (iso) => {
  const months = [
    "januar", "februar", "marts", "april", "maj", "juni",
    "juli", "august", "september", "oktober", "november", "december"];

  const d = new Date(iso);
  return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const ContentBlock = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-slate-900 mt-12 mb-4 leading-tight">
          {block.text}
        </h2>);

    case "h3":
      return (
        <h3 className="font-manrope font-bold text-xl text-slate-900 mt-8 mb-3 leading-tight">
          {block.text}
        </h3>);

    case "p":
    default:
      return (
        <p className="text-slate-700 text-lg leading-relaxed mb-5">
          {block.text}
        </p>);

  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "datePublished": post.date,
    "dateModified": post.date,
    "inLanguage": "da-DK",
    "author": {
      "@type": "Person",
      "@id": "https://valubi.dk/#founder",
      "name": "Jonathan Midjord Jørgensen"
    },
    "publisher": { "@id": "https://valubi.dk/#organization" },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://valubi.dk/blog/${post.slug}`
    },
    "keywords": post.keywords.join(", ")
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <SEO
        noindex
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        ogType="article"
        jsonLd={articleJsonLd} />

      <Navigation />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12 lg:pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-teal-600 text-sm font-medium mb-8 transition-colors"
          data-testid="post-back-link">

          <ArrowLeft size={16} />
          Tilbage til Viden
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10">

          <div className="inline-flex items-center px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-manrope font-semibold uppercase tracking-wider mb-5">
            {post.category}
          </div>
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-6 leading-tight break-words hyphens-auto" lang="da">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-slate-500 text-sm">
            <div className="flex items-center gap-1.5">
              <User size={14} />
              <span>Jonathan Midjord Jørgensen</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar size={14} />
              <time dateTime={post.date}>{formatDate(post.date)}</time>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={14} />
              <span>{post.readingTime}</span>
            </div>
          </div>
        </motion.header>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="prose-content"
          data-testid="post-content">

          {post.content.map((block, idx) =>
          <ContentBlock key={`${block.type}-${idx}`} block={block} />
          )}
        </motion.div>

        {/* CTA box */}
        <div
          className="mt-16 bg-gradient-to-br from-slate-50 to-teal-50/40 rounded-2xl p-8 border border-teal-100"
          data-testid="post-cta-box">

          <h3 className="font-manrope font-bold text-xl text-slate-900 mb-3">
            Skal vi hjælpe dig med BI-rapportering?
          </h3>
          <p className="text-slate-600 mb-6 leading-relaxed">
            Hent vores gratis Power BI skabeloner til Business Central eller
            e-conomic, eller book et uforpligtende møde. Vi bygger jeres data
            warehouse eller AI-assisterede BI-platform til fast pris.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/skabeloner"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-manrope font-semibold rounded-xl transition-colors"
              data-testid="post-cta-templates">

              Hent gratis templates
              <ArrowRight size={18} />
            </Link>
            <a
              href="/#booking"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-manrope font-semibold rounded-xl border border-slate-200 transition-colors"
              data-testid="post-cta-booking">

              Book et møde
            </a>
          </div>
        </div>
      </article>

      {/* Related posts */}
      <section className="bg-slate-50 border-t border-slate-100 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-slate-900 mb-8">
            Andre artikler
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) =>
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group block bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-300 hover:shadow-md transition-all"
              data-testid={`related-post-${p.slug}`}>

                <div className="inline-flex items-center px-2.5 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-manrope font-semibold uppercase tracking-wider mb-3">
                  {p.category}
                </div>
                <h3 className="font-manrope font-bold text-lg text-slate-900 group-hover:text-teal-600 transition-colors leading-snug break-words hyphens-auto" lang="da">
                  {p.title}
                </h3>
              </Link>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Valubi ApS — CVR 45291745
          </p>
          <Link
            to="/"
            className="text-slate-500 hover:text-teal-600 text-sm transition-colors">

            valubi.dk
          </Link>
        </div>
      </footer>
    </div>);

};

export default BlogPostPage;
